import { Component, Inject, OnDestroy, OnInit, ViewChild, TemplateRef } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Platform } from '@angular/cdk/platform';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription, interval, timer } from 'rxjs';
import { takeUntil, startWith, switchMap, tap, finalize, take } from 'rxjs/operators';

import { FuseConfigService } from '@fuse/services/config.service';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseSidebarService } from '@fuse/components/sidebar/sidebar.service';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';

import { navigation } from 'app/navigation/navigation';
import { locale as navigationEnglish } from 'app/navigation/i18n/en';
import { locale as navigationFrench } from 'app/navigation/i18n/fr';
import { locale as English } from 'app/i18n/en';
import { locale as French } from 'app/i18n/fr';
import { AuthService } from './main/auth/auth.service';
import { Store } from '@ngrx/store';
import { AuthState } from './main/auth/store/auth.reducer';
import { PrimeNGConfig } from 'primeng/api';
import { DateAdapter } from '@angular/material/core';
import { SpinnerService } from './main/pages/spinner/spinner.service';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
@Component({
    selector: 'app',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
    fuseConfig: any;
    navigation: any;
    visibleSpinner$: Observable<boolean>

    // Private
    private _unsubscribeAll: Subject<any>;

    @ViewChild('internetSnackbar') internetSnackbarTemplate: TemplateRef<any>;
    @ViewChild('internetReconnectedSnackbar') internetReconnectedSnackbarTemplate: TemplateRef<any>;
    private snackBarRef: MatSnackBarRef<any>;
    private isInternetConnected = true;
    private lastConnectionLossTime: number | null = null;

    /**
     * Constructor
     *
     * @param {DOCUMENT} document
     * @param {FuseConfigService} _fuseConfigService
     * @param {FuseNavigationService} _fuseNavigationService
     * @param {FuseSidebarService} _fuseSidebarService
     * @param {FuseSplashScreenService} _fuseSplashScreenService
     * @param {FuseTranslationLoaderService} _fuseTranslationLoaderService
     * @param {Platform} _platform
     * @param {TranslateService} _translateService
     */
    constructor(
        @Inject(DOCUMENT) private document: any,
        private _fuseConfigService: FuseConfigService,
        private _fuseNavigationService: FuseNavigationService,
        private _fuseSidebarService: FuseSidebarService,
        private _fuseSplashScreenService: FuseSplashScreenService,
        private _fuseTranslationLoaderService: FuseTranslationLoaderService,
        private _translateService: TranslateService,
        private _platform: Platform,
        private _authService: AuthService,
        private _store: Store<AuthState>,
        private primeNGConfig: PrimeNGConfig,
        private dateAdapter: DateAdapter<Date>,
        private _spinnerService: SpinnerService,
        private _snackBar: MatSnackBar
    ) {
        // Get default navigation
        this.navigation = navigation;

        // Register the navigation to the service
        this._fuseNavigationService.register('main', this.navigation);

        // Set the main navigation as our current navigation
        this._fuseNavigationService.setCurrentNavigation('main');

        // Add languages
        this._translateService.addLangs(['en', 'fr']);

        // Set the default language
        if (this._translateService.getLangs().indexOf(this._translateService.getBrowserLang()) >= 0) {
            this._translateService.setDefaultLang(this._translateService.getBrowserLang());
        } else {
            this._translateService.setDefaultLang('en');
        }

        // Set the navigation translations
        this._fuseTranslationLoaderService.loadTranslations(navigationEnglish, navigationFrench, English, French);

        // Use a language
        let lang = localStorage.getItem('lang');
        if (!lang || this._translateService.getLangs().indexOf(lang) == -1) {
            lang = this._translateService.getDefaultLang();
            localStorage.setItem('lang', lang);
        }
        this._translateService.use(lang);
        this.dateAdapter.setLocale(lang);
        this._translateService.get('App.PrimeNGConfig').subscribe(res => this.primeNGConfig.setTranslation(res));

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix Start
         * ----------------------------------------------------------------------------------------------------
         */

        /**
         * If you are using a language other than the default one, i.e. Turkish in this case,
         * you may encounter an issue where some of the components are not actually being
         * translated when your app first initialized.
         *
         * This is related to ngxTranslate module and below there is a temporary fix while we
         * are moving the multi language implementation over to the Angular's core language
         * service.
         **/

        // Set the default language to 'en' and then back to 'tr'.
        // '.use' cannot be used here as ngxTranslate won't switch to a language that's already
        // been selected and there is no way to force it, so we overcome the issue by switching
        // the default language back and forth.
        /**
         setTimeout(() => {
            this._translateService.setDefaultLang('en');
            this._translateService.setDefaultLang('tr');
         });
         */

        /**
         * ----------------------------------------------------------------------------------------------------
         * ngxTranslate Fix End
         * ----------------------------------------------------------------------------------------------------
         */

        // Add is-mobile class to the body if the platform is mobile
        if (this._platform.ANDROID || this._platform.IOS) {
            this.document.body.classList.add('is-mobile');
        }

        // Set the private defaults
        this._unsubscribeAll = new Subject();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        this._authService.autoLogin();

        this.monitorInternetConnection();
        // Subscribe to config changes
        this._fuseConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config) => {

                this.fuseConfig = config;

                // Boxed
                if (this.fuseConfig.layout.width === 'boxed') {
                    this.document.body.classList.add('boxed');
                }
                else {
                    this.document.body.classList.remove('boxed');
                }

                // Color theme - Use normal for loop for IE11 compatibility
                for (let i = 0; i < this.document.body.classList.length; i++) {
                    const className = this.document.body.classList[i];

                    if (className.startsWith('theme-')) {
                        this.document.body.classList.remove(className);
                    }
                }

                this.document.body.classList.add(this.fuseConfig.colorTheme);
            });

        this.visibleSpinner$ = this._spinnerService.visible;


    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        if (this.snackBarRef) {
            this.snackBarRef.dismiss();
        }

    }

    // @HostListener('window:beforeunload', ['$event'])
    // beforeUnloadHandler(event) {
    //     console.log(event);
    //     if(localStorage.getItem("remember") == "false"){
    //         this._store.dispatch(new AuthActions.DoLogoutAction());
    //     }
    // }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
    }

    /* private monitorInternetConnection(): void {
    let consecutiveSuccessCount = 0;
    let currentInterval = 5000; 
    let checkTimer: any;
    let isChecking = false;

    
    const fetchWithTimeout = (url: string, timeout = 4500): Promise<Response> => {
        
        const cacheBuster = `?_=${Date.now()}`;
        const headers = new Headers({
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
        });

        return Promise.race([
            fetch(url + cacheBuster, { 
                method: 'GET',
                headers: headers,
                cache: 'no-store'
            }),
            new Promise<never>((_, reject) => 
                setTimeout(() => reject(new Error('Timeout')), timeout))
        ]);
    };

    const performCheck = async () => {
        if (isChecking) return;
        isChecking = true;

        try {
            await fetchWithTimeout('https://jsonplaceholder.typicode.com/posts/1');
            handleStatusChange(true);
        } catch (error) {
            handleStatusChange(false);
        } finally {
            isChecking = false;
            scheduleNextCheck();
        }
    };

    const handleStatusChange = (newStatus: boolean) => {
        if (newStatus) {
            consecutiveSuccessCount++;
            if (consecutiveSuccessCount >= 5 && currentInterval === 5000) {
                currentInterval = 15000;
                consecutiveSuccessCount = 0;
                console.log('Passage à l\'intervalle de 15s');
            }
        } else {
            consecutiveSuccessCount = 0;
            if (currentInterval === 15000) {
                currentInterval = 5000;
                console.log('Retour à l\'intervalle de 5s');
            }
        }

        if (newStatus !== this.isInternetConnected) {
            this.isInternetConnected = newStatus;
            this.handleConnectionChange(newStatus);
        }
    };

    const scheduleNextCheck = () => {
        if (checkTimer) {
            clearTimeout(checkTimer);
        }
        checkTimer = setTimeout(() => {
            performCheck();
        }, currentInterval);
    };

    
    performCheck();

    
    this._unsubscribeAll.subscribe(() => {
        clearTimeout(checkTimer);
    });
} */

    private monitorInternetConnection(): void {
        let consecutiveSuccessCount = 0;
        let currentInterval = 3000; 0
        let checkTimer: any;
        let isChecking = false;
        let lastConnectionLossTime: number | null = null;
        let isDisconnectSnackbarShown = false;

        const fetchWithTimeout = (url: string, timeout = 5000): Promise<Response> => {
            const cacheBuster = `?_=${Date.now()}`;
            const headers = new Headers({
                'Cache-Control': 'no-cache, no-store, must-revalidate',
                'Pragma': 'no-cache',
                'Expires': '0'
            });

            return Promise.race([
                fetch(url + cacheBuster, {
                    method: 'GET',
                    headers: headers,
                    cache: 'no-store'
                }),
                new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('Timeout')), timeout))
            ]);
        };

        const performCheck = async () => {
            if (isChecking) return;
            isChecking = true;

            try {
                await fetchWithTimeout('https://jsonplaceholder.typicode.com/posts/1');
                handleStatusChange(true);
            } catch (error) {
                handleStatusChange(false);
            } finally {
                isChecking = false;
                scheduleNextCheck();
            }
        };

        const handleStatusChange = (newStatus: boolean) => {
            const now = Date.now();

            if (newStatus) {
                consecutiveSuccessCount++;
                lastConnectionLossTime = null; 
                if (consecutiveSuccessCount >= 5 && currentInterval === 3000) {
                    currentInterval = 15000; 
                    console.log('Passage à l\'intervalle de 15s');
                }
                if (!this.isInternetConnected) {
                    this.isInternetConnected = true;
                    isDisconnectSnackbarShown = false; 
                    this.handleConnectionChange(true); 
                }
            } else {
                consecutiveSuccessCount = 0;
                if (!lastConnectionLossTime) {
                    lastConnectionLossTime = now; 
                }
               
                if (currentInterval === 15000) {
                    currentInterval = 3000;
                    console.log('Retour à l\'intervalle de 3s suite à une coupure');
                }

               
                if (lastConnectionLossTime && now - lastConnectionLossTime >= 6000 && this.isInternetConnected && !isDisconnectSnackbarShown) {
                    this.isInternetConnected = false;
                    this.handleConnectionChange(false);
                }
            }
        };

        const scheduleNextCheck = () => {
            if (checkTimer) {
                clearTimeout(checkTimer);
            }
            checkTimer = setTimeout(() => {
                performCheck();
            }, currentInterval);
        };

        performCheck();

        this._unsubscribeAll.subscribe(() => {
            clearTimeout(checkTimer);
        });
    }
    private handleConnectionChange(isConnected: boolean): void {
        if (!isConnected) {
            this.snackBarRef = this._snackBar.openFromTemplate(this.internetSnackbarTemplate, {
                duration: undefined,
                panelClass: ['error-snackbar'],
                verticalPosition: 'top',
                horizontalPosition: 'center',
                politeness: 'assertive'
            });
        } else {
            if (this.snackBarRef) {
                this.snackBarRef.dismiss();
                this.snackBarRef = null;
            }
            this.snackBarRef = this._snackBar.openFromTemplate(this.internetReconnectedSnackbarTemplate, {
                duration: 3000,
                panelClass: ['success-snackbar'],
                verticalPosition: 'top',
                horizontalPosition: 'center',
                politeness: 'polite'
            });
        }
    }
}
